import * as React from "react";
import { Box } from "../box";

export interface IAnchor {
  /**
   *  If `outside-govlaunch`, rel noopener noreferrer will be used, otherwise only target="_blank"
   */
  external?: "outside-govlaunch" | "outside-redirect" | "govlaunch";
  /**
   * If `true`, the link will be disabled and not tabbable
   */
  disabled?: boolean;
}

const baseStyleProps = {
  transition: `all 0.15s ease-out`,
  cursor: "pointer",
  textDecoration: "none",
  outline: "none",
  color: "blue.600",
  _focus: {
    boxShadow: "outline",
  },
  _disabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    textDecoration: "none",
  },
};

const AnchorBox = Box.withComponent("a");

export type AnchorProps = IAnchor & React.ComponentProps<typeof AnchorBox>;

export const Anchor = React.forwardRef<HTMLAnchorElement, AnchorProps>(
  ({ disabled, external, onClick, href, target, ...rest }, ref) => {
    if (target === "_blank") {
      console.warn('You should not use target directly, please use `external="outside-govlaunch"`');
    }

    return (
      <AnchorBox
        as="a"
        d="inline-flex"
        ref={ref}
        href={getSecureHref(href, external)}
        target={target}
        tabIndex={disabled ? -1 : undefined}
        aria-disabled={disabled}
        onClick={event => {
          if (disabled) {
            event.preventDefault();
            return;
          }

          if (onClick) {
            onClick(event);
          }
        }}
        _hover={{ textDecoration: "underline" }}
        {...getExternalProps(external)}
        {...baseStyleProps}
        {...rest}
      />
    );
  },
);

Anchor.displayName = "Anchor";

export function getExternalProps(external: IAnchor["external"]) {
  if (external === "outside-govlaunch") {
    return {
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }

  if (external === "outside-redirect") {
    return {
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }

  if (external === "govlaunch") {
    return {
      target: "_blank",
    };
  }

  return null;
}

export function getSecureRedirect(url: string | undefined, external: IAnchor["external"]) {
  if (external === "outside-govlaunch") {
    return `https://secure-urls.govlaunch.com/redirect?url=${url}`;
  }

  return url;
}

export function getSecureContent(url: string | undefined, external: IAnchor["external"]) {
  if (external === "outside-govlaunch") {
    return `https://secure-urls.govlaunch.com/content?url=${url}`;
  }

  return url;
}

export function getSecureHref(url: string | undefined, external: IAnchor["external"]) {
  if (!url) {
    return "#";
  }

  if (external === "outside-govlaunch") {
    return `https://secure-urls.govlaunch.com/?url=${url}`;
  }

  if (external === "outside-redirect") {
    return `https://secure-urls.govlaunch.com/redirect?url=${url}`;
  }

  return url;
}
